import React, { ReactElement } from "react";
import { Control, Controller, FormState, UseFormWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { domainValidation } from "constants/regexp";

import { IDockerTypeVersions } from "types/api/dockerApiInterface";

type controlType = {
 site_name: string;
 app_type: string;
 app_version: string;
 replicas: number;
};

const ClusterAppCustomComponent = ({
 control,
 formState,
 appVers,
 watch,
 handleOpen,
 handleReset,
 setValue
}: {
 control: Control<controlType>;
 formState: FormState<controlType>;
 appVers: IDockerTypeVersions | null;
 watch: UseFormWatch<controlType>;
 handleOpen: () => void;
 handleReset: () => void;
 setValue: (type: keyof controlType, value: string | number) => void;
}): ReactElement => {
 const intl = useIntl();

 const checkDisableConfirm = () => {
  return (
   watch("site_name") === "" ||
   !domainValidation.test(watch("site_name")) ||
   watch("app_type") === "" ||
   watch("app_version") === "" ||
   watch("replicas") <= 0
  );
 };

 return (
  <Stack spacing={2}>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.siteName" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.siteNameMessage" />
      </Typography>
      <Controller
       name="site_name"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("site_name")}
         onChange={({ currentTarget }) => setValue("site_name", currentTarget.value.toLowerCase())}
         label={intl.formatMessage({ id: "docker.website.add.siteName" })}
         error={formState.isDirty && !!formState?.errors?.site_name}
         InputLabelProps={{ shrink: true }}
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !!formState?.errors?.site_name &&
          intl.formatMessage({ id: "login.passwordMinLength" })
         }
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.appVersion" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.appVersionMessage" />
      </Typography>
      {appVers ? (
       <Controller
        name="app_version"
        control={control}
        render={({ field }) => (
         <Autocomplete
          fullWidth={true}
          autoHighlight
          sx={{ my: 2 }}
          onChange={(e, value) => {
           setValue("app_version", value?.label || "");
          }}
          options={appVers?.versions
           .filter((element) =>
            watch("app_type") === "php"
             ? element.name.includes(watch("app_type")) && !element.name.includes("litespeed")
             : element.name.includes(watch("app_type"))
           )
           .map((app) => {
            return { label: app.name, id: app.id };
           })}
          value={appVers?.versions
           .map((element) => {
            return { label: element.name, id: element.id };
           })
           .find((app) => app.label === watch("app_version"))}
          renderInput={(params) => (
           <TextField
            {...params}
            {...field}
            label={<FormattedMessage id="docker.website.add.appVersion" />}
            error={formState.isDirty && !!formState?.errors?.app_version}
            InputLabelProps={{ shrink: true }}
           />
          )}
         />
        )}
       />
      ) : (
       <></>
      )}
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="cluster.add.setReplicas" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="cluster.add.setReplicasMessage" />
      </Typography>
      <Controller
       name="replicas"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("replicas")}
         onChange={({ currentTarget }) => setValue("replicas", Number(currentTarget.value))}
         label={intl.formatMessage({ id: "cluster.add.replicas" })}
         error={formState.isDirty && !!formState?.errors?.replicas}
         InputLabelProps={{ shrink: true }}
         type="number"
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !!formState?.errors?.replicas &&
          intl.formatMessage({ id: "cluster.add.replicasError" })
         }
        />
       )}
      />
     </Stack>
     <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
       <FormattedMessage id="app.wizard.reset" />
      </Button>
      <Button sx={{ mt: 1, mr: 1 }} disabled={checkDisableConfirm()} onClick={handleOpen}>
       <FormattedMessage id="app.wizard.confirm" />
      </Button>
     </Stack>
    </Stack>
   </Paper>
  </Stack>
 );
};

export default ClusterAppCustomComponent;
